import { useAuth0 } from "@auth0/auth0-react";
import { useFormikContext } from "formik";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTheme } from "../../contexts/ThemeContext";
import { Loader } from "../common/progress/Loader";
import { ExtendedUser } from "../identity/models/ExtendedUser";
import { usePluginActions } from "./hooks/usePluginActions";
import { PluginDetailsExtended } from "./models/PluginDetailsExtended";
import { Status } from "./models/Status";
import { pluginValidationSchema } from "./schemas/PluginValidationSchema";
import { useLocalization } from "../../contexts/LocalizationContext";
import { useParams } from "react-router-dom";
import { processMarkdownToHTML } from "../../utils";
import { Action } from "./models/Action";

type PublishedPluginButtonGroupProps = { plugin: PluginDetailsExtended };

export const PublishedPluginButtonGroup = ({ plugin }: PublishedPluginButtonGroupProps) => {
  const { buttonStyle } = useTheme();
  const { user } = useAuth0<ExtendedUser>();
  const { isLoading, pluginActions } = usePluginActions();
  const { values, setTouched, handleSubmit } = useFormikContext<PluginDetailsExtended>();
  const { translate } = useLocalization();
  const params = useParams();
  const isSystemAdmin = user?.user_metadata?.isSystemAdmin;
  const pluginId = params.pluginId as string;

  const handleSave = async () => {
    try {
      values.htmlDescription = await processMarkdownToHTML(values.markdownDescription, values.descriptionResources);
      values.htmlDocumentation = await processMarkdownToHTML(
        values.markdownDocumentation,
        values.documentationResources
      );
      await pluginValidationSchema(translate).validate(values, { abortEarly: false });
      setTouched({});
      if (isSystemAdmin) await pluginActions.handleUpdate(parseInt(pluginId), values, Action.Save);
      else await pluginActions.handleUpdatePending(parseInt(pluginId), values);
    } catch (errors) {
      handleSubmit();
    }
  };

  if (isLoading) return <Loader />;

  return (
    <>
      <Button variant="success" className="me-2" type="button" style={buttonStyle} onClick={handleSave}>
        {values.publishToRWSAppStore && !isSystemAdmin ? translate("plugins.submit") : translate("common.save")}
      </Button>
      {plugin.status === Status.Published && (
        <Button
          variant="warning"
          className="me-2"
          type="button"
          onClick={() => pluginActions.changeStatus(Status.NotPublished, parseInt(pluginId))}
        >
          {translate("plugins.deactivate")}
        </Button>
      )}
      {plugin.status === Status.NotPublished && (
        <OverlayTrigger overlay={<Tooltip>{translate("versions.oneVersionShouldBeActive")}</Tooltip>}>
          <span className="d-inline-block">
            <Button
              variant="warning"
              className="me-2"
              disabled
              onClick={() => pluginActions.changeStatus(Status.Published, parseInt(pluginId))}
            >
              {translate("plugins.activate")}
            </Button>
          </span>
        </OverlayTrigger>
      )}
    </>
  );
};
